import React, { PureComponent } from "react";
import clsx from "clsx";

import {
  ComposedChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  Line,
  YAxis,
  XAxis,
  CartesianGrid,
  ReferenceDot,
  Cell,
} from "recharts";

import {
  CustomizedXAxisTickTime,
  CustomizedYAxisTickRightCurrency,
  CustomizedYAxisTickLeftCurrency,
  StandardTooltip,
  selectFormat,
  Error,
  isDateFromDataInISOFormat,
  formatDateFromData,
  MAIN_COLORS,
  getAllKeys,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "../Utils/XrayUtils.js";

import {
  getXrayTooltipPosition,
  findParentNodeByClass,
  closeAllTooltips,
} from "helpers/DOM";

const COLORS = {
  "Direct Rev": MAIN_COLORS[0],
  "Indirect Rev": MAIN_COLORS[1],

  "Direct eCPM": MAIN_COLORS[3], // impressions (lines)
  "Indirect eCPM": MAIN_COLORS[4], // impressions (lines)
  "Net eCPM": MAIN_COLORS[5], // impressions (lines)
};

class PocketWatchRevenueVsCPM extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
      widgetsData: props.data.data,
      widgetsInsights: [],
    };
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllTooltips("xray-tooltip-close-icon");

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  renderBars = (data, bars) => {
    let Allbars = [];
    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data.length <= 7;
    const labels = [];

    [...new Set(sortedKeys)].forEach((bar, i) => {
      if (bars.includes(bar))
        Allbars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId="stack"
            barSize={isFixedBarWidth ? 35 : null}
            fill={COLORS[bar]}
            yAxisId="left"
            isAnimationActive={false}
            cursor="pointer"
            background={
              i === 0 ? (
                <RenderBackground
                  insights={insights}
                  showIndicator={!this.state.tooltip.show}
                  node={this.node}
                  i={i}
                />
              ) : null
            }
            onClick={(d) => {
              const label = labels.filter((l) => l.date.date === d.date.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: COLORS[k],
                }))
                .filter((o) => o.value !== undefined);

              d.extra = extra;
              d.insight = label.length ? label[0] : null;

              d.posts = d.posts || [];

              this.clickBar(d, bar);
            }}
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  ></Cell>
                )
            )}
          </Bar>
        );
    });

    return Allbars;
  };

  renderLines = (data, lines) => {
    let allLines = [];
    const sortedKeys = this.getSortedKeys(data);

    [...new Set(sortedKeys)].forEach((line, i) => {
      if (lines.includes(line)) {
        allLines.push(
          <Line
            key={i}
            yAxisId="right"
            type="monotone"
            dataKey={line}
            stroke={COLORS[line]}
            strokeWidth={1}
            dot={false}
          />
        );
      }
    });

    return allLines;
  };

  legendElement = (data, lines) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        {lines.includes(entry) ? (
          <span
            className="legend__icon legend__icon--linechart"
            style={{ color: COLORS[entry] }}
          >
            &mdash;
          </span>
        ) : (
          <span
            className="legend__icon"
            style={{ background: COLORS[entry] }}
          ></span>
        )}

        <span className="legend__text" style={{ color: COLORS[entry] }}>
          {entry.replace("_", " ")}
        </span>
      </span>
    ));

  renderLegends = (data, lines) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = this.getSortedKeys(data);
    sortedKeys.forEach((source) => all_legends.push(source));

    legends = [...new Set(all_legends)];
    return this.legendElement(legends, lines);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .map((d) => d.key);
  }

  getExportData = () => ({
    data: this.props.data.data,
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  clickBar(d, dataKey) {
    if (
      this.state.tooltip.show &&
      d.date.date === this.state.tooltip.date.date
    ) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllTooltips("xray-tooltip-close-icon");
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllTooltips("xray-tooltip-close-icon");
  };

  render() {
    const { data, title } = this.props;

    const { widgetsInsights } = this.state;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    const lines = ["Direct eCPM", "Indirect eCPM", "Net eCPM"];
    const bars = ["Direct Rev", "Indirect Rev"];

    let widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={370} />;
    }

    const preparedData = formatDateFromData(widget_data, "YYYY/MM/DD");

    return (
      <div ref={this.divPosition}>
        {this.renderLegends(preparedData, lines)}
        <div className="mt-2 d-flex">
          <ResponsiveContainer height={370} width="100%">
            <ComposedChart
              data={preparedData}
              margin={{ top: 20, right: -40, bottom: 25, left: -38 }}
              stackOffset="sign"
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => {
                  return (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      yAxisId="right"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                          // tall widgets need custom height
                        />
                      }
                    />
                  );
                })}
              {!this.state.tooltip.show && (
                <Tooltip
                  // cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          let sortedUniquePayload = payload.filter(
                            (v, i, a) =>
                              a.findIndex((t) => t.dataKey === v.dataKey) === i
                          );

                          sortedUniquePayload = bars
                            .concat(lines)
                            .map((dataKey) =>
                              sortedUniquePayload.find(
                                (k) => k.dataKey === dataKey
                              )
                            );

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  {tooltip_data.date}
                                </span>
                              </header>
                              <main className="standard-tooltip__main">
                                {sortedUniquePayload.map((key, i) => {
                                  return (
                                    <div
                                      className="standard-tooltip__item"
                                      key={i}
                                    >
                                      {lines.includes(key.dataKey) ? (
                                        <span
                                          className="standard-tooltip__icon standard-tooltip__icon--linechart"
                                          style={{ color: key.color }}
                                        >
                                          &mdash;
                                        </span>
                                      ) : (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{ background: key.color }}
                                        ></span>
                                      )}

                                      <span
                                        className={clsx(
                                          "standard-tooltip__key",
                                          "text-capitalize"
                                        )}
                                        style={{ color: key.color }}
                                      >
                                        {key.dataKey.replace("_", " ")}
                                      </span>

                                      <span className="standard-tooltip__value">
                                        {selectFormat("currency", key.value)}
                                      </span>
                                    </div>
                                  );
                                })}

                                {widgetsInsights.length > 0 &&
                                  !this.state.tooltip.show && (
                                    <InsightsWithinTooltip
                                      data={widgetsInsights}
                                      tooltipDate={tooltip_data.date}
                                    />
                                  )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              {this.renderBars(preparedData, bars)}
              {this.renderLines(preparedData, lines)}

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTime />}
              />

              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickLeftCurrency />}
              />

              <YAxis
                yAxisId="right"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickRightCurrency />}
                orientation="right"
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
        <div
          className="xray-tooltip-container"
          style={{
            top: "80px", //(this.state.tooltip.y - 20) + 'px',
            left:
              this.state.tooltip.x + this.state.tooltip.width + 220 <
              this.state.width
                ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                : this.state.tooltip.x -
                  getXrayTooltipPosition(
                    this.divPosition.current,
                    this.state.tooltip
                  ) +
                  "px",
            opacity: this.state.tooltip.show ? 1.0 : 0.0,
            visibility: this.state.tooltip.show ? "visible" : "hidden",
          }}
        >
          <XRayTooltip
            data={this.state.tooltip}
            insights={widgetsInsights}
            close={this.closeTooltip.bind(this)}
          />
        </div>
      </div>
    );
  }
}

export default PocketWatchRevenueVsCPM;
